import { MutationHookOptions } from '@apollo/client';

import {
  ListCreateMutationCacheConfig,
  ListDeleteMutationCacheConfig,
  ListUpdateMutationCacheConfig,
  buildListCreateCacheUpdate,
  buildListDeleteCacheUpdate,
  buildListUpdateCacheUpdate,
} from './createListMutationCacheUpdates';

type CreateMutationOptions<
  TData,
  TVariables,
  TItem extends Record<string, unknown>,
  TScope,
> = Pick<
  MutationHookOptions<TData, TVariables>,
  'awaitRefetchQueries' | 'optimisticResponse' | 'refetchQueries' | 'update'
> & {
  cacheUpdate?: ListCreateMutationCacheConfig<TItem, TScope, TData, TVariables>;
};

type UpdateMutationOptions<
  TData,
  TVariables,
  TItem extends Record<string, unknown>,
  TScope,
> = Pick<
  MutationHookOptions<TData, TVariables>,
  'awaitRefetchQueries' | 'fetchPolicy' | 'refetchQueries' | 'update'
> & {
  cacheUpdate?: ListUpdateMutationCacheConfig<TItem, TScope, TData>;
};

type DeleteMutationOptions<
  TData,
  TVariables,
  TItem extends Record<string, unknown>,
  TScope,
> = Pick<
  MutationHookOptions<TData, TVariables>,
  'awaitRefetchQueries' | 'refetchQueries' | 'update'
> & {
  cacheUpdate?: ListDeleteMutationCacheConfig<TItem, TScope, TVariables>;
};

const resolveMutationOptions = <TCacheUpdate>(
  buildUpdate: (cacheUpdate: TCacheUpdate) => unknown,
  options?: {
    cacheUpdate?: TCacheUpdate;
    update?: unknown;
  },
) => {
  if (!options?.cacheUpdate) {
    return options;
  }

  const { cacheUpdate, update, ...rest } = options;

  return {
    ...rest,
    update: update ?? buildUpdate(cacheUpdate),
  };
};

export const resolveCreateMutationOptions = <
  TData,
  TVariables,
  TItem extends Record<string, unknown> = Record<string, unknown>,
  TScope = unknown,
>(
  options?: CreateMutationOptions<TData, TVariables, TItem, TScope>,
):
  | Pick<
      MutationHookOptions<TData, TVariables>,
      'awaitRefetchQueries' | 'optimisticResponse' | 'refetchQueries' | 'update'
    >
  | undefined =>
  resolveMutationOptions(buildListCreateCacheUpdate, options) as
    | Pick<
        MutationHookOptions<TData, TVariables>,
        | 'awaitRefetchQueries'
        | 'optimisticResponse'
        | 'refetchQueries'
        | 'update'
      >
    | undefined;

export const resolveUpdateMutationOptions = <
  TData,
  TVariables,
  TItem extends Record<string, unknown> = Record<string, unknown>,
  TScope = unknown,
>(
  options?: UpdateMutationOptions<TData, TVariables, TItem, TScope>,
):
  | Pick<
      MutationHookOptions<TData, TVariables>,
      'awaitRefetchQueries' | 'fetchPolicy' | 'refetchQueries' | 'update'
    >
  | undefined =>
  resolveMutationOptions(buildListUpdateCacheUpdate, options) as
    | Pick<
        MutationHookOptions<TData, TVariables>,
        'awaitRefetchQueries' | 'fetchPolicy' | 'refetchQueries' | 'update'
      >
    | undefined;

export const resolveDeleteMutationOptions = <
  TData,
  TVariables,
  TItem extends Record<string, unknown> = Record<string, unknown>,
  TScope = unknown,
>(
  options?: DeleteMutationOptions<TData, TVariables, TItem, TScope>,
):
  | Pick<
      MutationHookOptions<TData, TVariables>,
      'awaitRefetchQueries' | 'refetchQueries' | 'update'
    >
  | undefined =>
  resolveMutationOptions(buildListDeleteCacheUpdate, options) as
    | Pick<
        MutationHookOptions<TData, TVariables>,
        'awaitRefetchQueries' | 'refetchQueries' | 'update'
      >
    | undefined;
